import { CommentSharp, Delete, InfoSharp } from "@mui/icons-material";
import { Badge, Box, Button, Tabs, Tab } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
	formatDate,
	formatDateTime,
	formatHoursMinutes,
} from "@repo/legacy/components/helper/Index";
import { useMyTimeApprovalsByProjectDataSource } from "@repo/legacy/components/shared/datasources/MyTimeApprovalsByProjectDataSource";
import ConformationDialogWithContext from "@repo/legacy/components/shared/dialogs/ConformationWithContext";
import DialogShowDescription from "@repo/legacy/components/shared/dialogs/DialogShowDescription";
import DialogTimeApproval from "@repo/legacy/components/shared/dialogs/DialogTimeApproval";
import DialogTimeApprovalDetails from "@repo/legacy/components/shared/dialogs/DialogTimeApprovalDetails";
import DetailPanelContentTimeApprovals from "@repo/legacy/components/shared/lists/DetailPanelContentTimeApprovals";
import List from "@repo/legacy/components/shared/lists/List";
import {
	ApprovalTimeEntryStatus,
	type Contact,
	type Project,
	type ProjectTimeApproval,
	type ProjectTimeApprovalHistory,
	type ProjectTimeSummary,
	useDownloadTimeSheetLazyQuery,
	useGetMyAllProjectsLazyQuery,
	useGetMyProjectTimeSummariesLazyQuery,
} from "@repo/server-api";
import { DownloadButton, useMe } from "@repo/shared/components";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import React, { useEffect, useMemo, useState } from "react";

import ProjectProgressPieBox from "../../components/ProjectProgressPieBox";

import type { GridCellParams, GridColDef } from "@mui/x-data-grid";
import type { ProjectTimeApprovalExtended } from "@repo/server-api/types";

export function MyTimeSheets() {
	const { t, i18n } = useTranslation();

	const [openDialog, setOpenDialog] = useState<boolean>(false);
	const [openDialogDetails, setOpenDialogDetails] = useState<boolean>(false);
	const [openConformationDialog, setOpenConformationDialog] = useState<boolean>(false);
	const [openDialogComment, setOpenDialogComment] = useState<boolean>(false);
	const [timeApproval, setTimeApproval] = useState<ProjectTimeApprovalExtended | null>(null);

	// TODO Remove this after optimization
	const [timeApprovalComments, setTimeApprovalComments] =
		useState<ProjectTimeApprovalExtended | null>(null);
	const [timeApprovalDetails, setTimeApprovalDetails] =
		useState<ProjectTimeApprovalExtended | null>(null);
	const [timeApprovalDelete, setTimeApprovalDelete] = useState<ProjectTimeApproval | null>(null);

	const [message, setMessage] = useState<string>("");

	const [projects, setProjects] = useState<Project[]>([]);
	const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

	const myTimeApprovalsDataSource = useMyTimeApprovalsByProjectDataSource(selectedProjectId);

	const [year, setYear] = useState<dayjs.Dayjs>(dayjs());
	const [openYear, setOpenYear] = useState<boolean>(false);

	const [getMyProjects] = useGetMyAllProjectsLazyQuery({
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data.myAllProjects?.nodes) {
				const projectList = data.myAllProjects.nodes as Project[];
				// Sort projects by name
				const sortedProjects = [...projectList].sort((a, b) => a.name.localeCompare(b.name));
				setProjects(sortedProjects);

				console.log("Projects loaded:", sortedProjects);

				// Auto-select first project if none is selected
				if (!selectedProjectId && sortedProjects.length > 0) {
					setSelectedProjectId(sortedProjects[0]!.id);
				}
			}
		},
	});

	const [downloadTimeSheet] = useDownloadTimeSheetLazyQuery({
		fetchPolicy: "no-cache",
	});

	const [getProjectTimeSummaries] = useGetMyProjectTimeSummariesLazyQuery({
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data.myProjectTimeSummaries) {
				setProjectTimeSummary(data.myProjectTimeSummaries as ProjectTimeSummary[]);
			}
		},
	});

	const [projectTimeSummaries, setProjectTimeSummary] = useState<ProjectTimeSummary[] | null>(null);

	useEffect(() => {
		getMyProjects({
			variables: {
				start: dayjs(year).startOf("year").format("YYYY-MM-DD"),
				end: dayjs(year).endOf("year").format("YYYY-MM-DD"),
				first: 100,
			},
		});
	}, [year, getMyProjects]);

	useEffect(() => {
		if (selectedProjectId) {
			getProjectTimeSummaries({
				variables: {
					projectId: selectedProjectId,
				},
			});
			// Refresh time approvals when project changes
			myTimeApprovalsDataSource.refresh?.();
		} else {
			setProjectTimeSummary(null);
		}
	}, [selectedProjectId]);

	const columns: GridColDef<ProjectTimeApproval>[] = useMemo(
		() => [
			{
				field: "projectName",
				headerName: t("project-name"),
				width: 150,
				flex: 1,
				sortable: true,
				valueGetter: (value, row) => row.project?.name || "",
				renderCell: (params: GridCellParams) => {
					return params.row.project?.name ?? "";
				},
			},
			{
				field: "approvalNumber",
				headerName: t("approval-number"),
				width: 150,
				flex: 1,
				sortable: true,
				valueGetter: (value, row) => row.number,
				renderCell: (params: GridCellParams) => {
					return params.row.number;
				},
			},
			{
				field: "totalHours",
				headerName: t("total-hours"),
				width: 150,
				sortable: true,
				valueGetter: (value, row) => row.totalMinutes || 0,
				renderCell: (params: GridCellParams) => {
					return formatHoursMinutes(params.row.totalMinutes || 0);
				},
			},
			{
				field: "fromTo",
				headerName: t("start-till-end"),
				width: 250,
				headerAlign: "center",
				align: "center",
				sortable: true,
				valueGetter: (value, row) => {
					if (!row.from || !row.to) return "";
					const from = formatDate(row.from, i18n.language);
					const to = formatDate(row.to, i18n.language);
					return `${from} → ${to}`;
				},
				renderCell: (params: GridCellParams) => {
					if (!params.row.from || !params.row.until) return "";
					const from = formatDate(params.row.from, i18n.language);
					const to = formatDate(params.row.to, i18n.language);
					return `${from} → ${to}`;
				},
			},
			{
				field: "status",
				headerName: t("status"),
				width: 150,
				align: "center",
				sortable: true,
				valueGetter: (value, row) => row.status,
				renderCell: (params: GridCellParams) => {
					let fontColor = "#000";
					let bgColor = "#f9a825";
					switch (params.row.status) {
						case "APPROVED":
							fontColor = "#fff";
							bgColor = "#4caf50";
							break;
						case "DELETED":
						case "REJECTED":
							fontColor = "#000";
							bgColor = "#f44336";
							break;
						case "PENDING":
							fontColor = "#000";
							bgColor = "#f9a825";
							break;
						case "CANCELED":
							fontColor = "#000";
							bgColor = "#9e9e9e";
							break;
						case "OPEN":
							fontColor = "#000";
							bgColor = "lightblue";
							break;
						default:
							fontColor = "#000";
							bgColor = "#f9a825";
							break;
					}
					return (
						<div
							style={{
								justifyContent: "center",
								alignItems: "center",
								display: "flex",
								height: "100%",
							}}>
							<Badge
								style={{
									justifyContent: "center",
									alignItems: "center",
									backgroundColor: bgColor,
									color: fontColor,
									borderRadius: "10px",
									padding: "5px 10px",
									fontSize: "12px",
									fontWeight: "bold",
									width: "50px",
									height: "15px",
								}}>
								{t("time-sheets.status." + params.row.status.toLowerCase(), {
									ns: "enum",
								})}
							</Badge>
						</div>
					);
				},
			},
			{
				field: "createdBy",
				headerName: t("created-by"),
				width: 150,
				sortable: true,
				valueGetter: (value, row) => row.createdBy.displayName,
				renderCell: (params: GridCellParams) => {
					return params.row.createdBy.displayName;
				},
			},
			{
				field: "createdAt",
				headerName: t("created-at"),
				width: 150,
				sortable: true,
				valueGetter: (value, row) => formatDateTime(row.timestamp, i18n.language),
				renderCell: (params: GridCellParams) => {
					return formatDateTime(params.row.timestamp, i18n.language);
				},
			},
		],
		[t, i18n.language, myTimeApprovalsDataSource],
	);

	const mainColumns = useMemo(
		() => [
			...columns,
			{
				field: "actions",
				headerName: t("actions"),
				sortable: false,
				width: 250,
				renderCell: (params: GridCellParams) => {
					const openComment = (t: ProjectTimeApproval | null) => {
						setTimeApprovalComments(t);
						setOpenDialogComment(true);
					};

					const openDetails = (t: ProjectTimeApproval | null) => {
						setTimeApprovalDetails(t);
						setOpenDialogDetails(true);
					};

					const openDelete = (t: ProjectTimeApproval | null) => {
						setTimeApprovalDelete(t);
						setOpenConformationDialog(true);
					};

					const startDownload = async (approvalId: string) => {
						const result = await downloadTimeSheet({
							variables: {
								projectTimeApprovalId: approvalId,
							},
						});

						if (!result || !result.data) {
							throw new Error("Download failed");
						}

						const url = result.data?.downloadTimeSheet;
						if (url) {
							window.open(url, "_blank");
						}
					};

					return (
						<div>
							<DownloadButton onDownload={() => startDownload(params.row.id)} />
							<Button
								disabled={!params.row.description}
								data-testid="edit-button"
								onClick={() => openComment(params.row)}>
								<CommentSharp />
							</Button>
							<Button data-testid="edit-button" onClick={() => openDetails(params.row)}>
								<InfoSharp />
							</Button>
							<Button
								disabled={params.row.status !== ApprovalTimeEntryStatus.Pending}
								data-testid="edit-button"
								onClick={() => openDelete(params.row)}>
								<Delete />
							</Button>
						</div>
					);
				},
			},
		],
		[t, columns],
	);

	const detailsColumns = [
		{
			field: "space",
			headerName: "",
			width: 50,
		},

		...columns,

		{
			field: "actions",
			width: 250,
		},
	];

	const me = useMe();

	const refreshData = () => {
		myTimeApprovalsDataSource.refresh?.();
		if (selectedProjectId) {
			getProjectTimeSummaries({
				variables: {
					projectId: selectedProjectId,
				},
			});
		}
	};

	const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
		setSelectedProjectId(newValue);
	};

	const selectedProject = projects.find((p) => p.id === selectedProjectId);

	return (
		<Box sx={{ height: "100%", width: "100%" }}>
			<h3 className="purpleHeader">{t("my-time-sheets")}</h3>
			<Box sx={{ marginLeft: "20px", marginBottom: "20px" }}>
				<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language || "de"}>
					<DatePicker
						sx={{ width: "150px" }}
						open={openYear}
						onClose={() => setOpenYear(false)}
						onOpen={() => setOpenYear(true)}
						label={t("year")}
						views={["year"]}
						value={year}
						onChange={(newValue) => {
							setYear(newValue as dayjs.Dayjs);
						}}
						slotProps={{
							textField: {
								onClick: () => setOpenYear(!openYear),
							},
						}}
					/>
				</LocalizationProvider>
			</Box>

			{/* Project Tabs - only show if multiple projects */}
			{projects.length > 1 && (
				<Box
					sx={{ borderBottom: 1, borderColor: "divider", marginLeft: "20px", marginRight: "20px" }}>
					<Tabs
						value={selectedProjectId || false}
						onChange={handleTabChange}
						variant="scrollable"
						scrollButtons="auto">
						{projects.map((project) => (
							<Tab key={project.id} label={project.name} value={project.id} />
						))}
					</Tabs>
				</Box>
			)}

			{/* Show project name if only one project */}
			{projects.length === 1 && (
				<Box sx={{ marginLeft: "20px" }}>
					<h3 className="purpleFont font24">{projects[0]!.name}</h3>
				</Box>
			)}

			<DialogShowDescription
				open={openDialogComment}
				setOpen={setOpenDialogComment}
				item={timeApprovalComments}
			/>
			<DialogTimeApproval
				open={openDialog}
				setOpen={setOpenDialog}
				refreshData={refreshData}
				timeApproval={timeApproval}
				setTimeApproval={setTimeApproval}
			/>
			<DialogTimeApprovalDetails
				open={openDialogDetails}
				setOpen={setOpenDialogDetails}
				refreshData={refreshData}
				timeApproval={timeApprovalDetails}
				setTimeApproval={setTimeApprovalDetails}
				allMyTimeApprovalsDataSource={myTimeApprovalsDataSource}
			/>
			<ConformationDialogWithContext
				openPopup={openConformationDialog}
				setOpenDeleteConformation={setOpenConformationDialog}
				text={t("do-you-really-want-to-delete-this-time-approval-sheet")}
				message={message}
				setMessage={setMessage}
				handleYes={() => {
					if (myTimeApprovalsDataSource.deleteDataWithReason && timeApprovalDelete?.id)
						myTimeApprovalsDataSource.deleteDataWithReason(timeApprovalDelete.id, message);

					setTimeout(() => {
						refreshData();
					}, 500);

					setOpenConformationDialog(false);
				}}
				handleClose={() => {
					setOpenConformationDialog(false);
				}}
			/>
			<List
				showOnlyChildren={true}
				dataSource={myTimeApprovalsDataSource}
				columns={mainColumns as GridCellParams<ProjectTimeApproval>[]}
				hasDetailPanelContent={true}
				detailPanelContent={(row?: ProjectTimeApproval) =>
					row?.history.length ? (
						<DetailPanelContentTimeApprovals
							history={row?.history as ProjectTimeApprovalHistory[]}
							columns={detailsColumns as GridCellParams[]}
						/>
					) : null
				}
				startYearDate={dayjs(year).startOf("year").format("YYYY-MM-DD")}
				endYearDate={dayjs(year).endOf("year").format("YYYY-MM-DD")}>
				<Button
					disabled={!selectedProject}
					data-testid="add-button"
					variant="contained"
					onClick={() => {
						setTimeApproval({
							project: selectedProject as Project,
							until: dayjs().format("YYYY-MM-DD") as string,
							employee: me as Contact,
						} as unknown as ProjectTimeApprovalExtended);
						setOpenDialog(true);
					}}
					className="left blueButton"
					sx={{ ml: 2.5, mt: 2.5 }}
					color="secondary">
					{t("create-new-time-sheet")}
				</Button>
				<div className="clear" />
				{projectTimeSummaries && projects.length > 0 ? (
					<Box className="left w-90" sx={{ ml: 2.5, mt: 2.5 }}>
						<ProjectProgressPieBox projectTimeSummaries={projectTimeSummaries} />
					</Box>
				) : null}
			</List>
		</Box>
	);
}
